import { useState } from "react";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { UserPublicInfoDto } from "@/pages/admin/events/types";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Loading } from "@/components/ui/loading";

interface EmployeeSelectorProps {
  employees: UserPublicInfoDto[];
  isLoading: boolean;
  selectedEmployee: UserPublicInfoDto | null;
  onSelectEmployee: (employee: UserPublicInfoDto) => void;
  disabled?: boolean;
}

export function EmployeeSelector({
  employees,
  isLoading,
  selectedEmployee,
  onSelectEmployee,
  disabled = false,
}: EmployeeSelectorProps) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  // Create a safe copy of employees with proper structure verification
  const safeEmployees = Array.isArray(employees)
    ? employees.filter((emp) => emp && typeof emp === "object" && emp.id)
    : [];

  const filteredEmployees =
    searchTerm.trim() === ""
      ? safeEmployees
      : safeEmployees.filter((emp) =>
          `${emp.firstName || ""} ${emp.lastName || ""} ${emp.email || ""}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
        );

  return (
    <div className="space-y-2">
      <label htmlFor="employee" className="text-sm font-medium">
        Select Employee
      </label>
      {isLoading ? (
        <Loading size="sm" />
      ) : (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              className="w-full justify-between"
              disabled={disabled}
            >
              {selectedEmployee
                ? `${selectedEmployee.firstName || ""} ${
                    selectedEmployee.lastName || ""
                  }`
                : "Select employee..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          {open && (
            <PopoverContent className="w-[400px] p-2">
              <div className="space-y-2">
                <input
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                  placeholder="Search employees..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                {filteredEmployees.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No employees found.
                  </div>
                ) : (
                  <div className="max-h-[300px] overflow-y-auto">
                    {filteredEmployees.map((employee) => (
                      <div
                        key={employee.id}
                        className={cn(
                          "flex items-center px-2 py-1.5 rounded-md cursor-pointer hover:bg-accent hover:text-accent-foreground",
                          selectedEmployee?.id === employee.id &&
                            "bg-accent text-accent-foreground",
                        )}
                        onClick={() => {
                          onSelectEmployee(employee);
                          setOpen(false);
                        }}
                      >
                        <Check
                          className={cn(
                            "mr-2 h-4 w-4",
                            selectedEmployee?.id === employee.id
                              ? "opacity-100"
                              : "opacity-0",
                          )}
                        />
                        <div className="flex flex-col">
                          <span className="font-medium">
                            {employee.firstName || ""} {employee.lastName || ""}
                          </span>
                          {employee.email && (
                            <span className="text-xs text-muted-foreground">
                              {employee.email}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </PopoverContent>
          )}
        </Popover>
      )}
    </div>
  );
}
